import React from 'react';
import { Layout } from '../components/layout/Layout';
import { Download, Image, FileText, Palette, ExternalLink, Calendar } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// Assets URLs
const JOURNEE_LOGO = "https://static.prod-images.emergentagent.com/jobs/a27b2f16-dbd8-4903-b7cf-e0c3f4166774/images/def00b15247db958e0a1a006c0644ef388493b1419b5bc5be44742dac8edad62.png";
const OAR_LOGO = "https://customer-assets.emergentagent.com/job_a27b2f16-dbd8-4903-b7cf-e0c3f4166774/artifacts/zuafya4h_LOGO%20OAR%20jpeg.jpg";

const MediaKitPage = () => {
  const { t } = useLanguage();
  
  const colors = [
    { name: "Bleu OAR", hex: "#0039A6", textColor: "white" },
    { name: "Or / Gold", hex: "#D4AF37", textColor: "black" },
    { name: "Rouge Foncé", hex: "#8B0000", textColor: "white" },
    { name: "Orange", hex: "#E65100", textColor: "white" },
    { name: "Crème", hex: "#FAF8F5", textColor: "black" },
    { name: "Vert Afrique", hex: "#2E7D32", textColor: "white" },
  ];
  
  const socialBanners = [
    { name: "Facebook Cover", size: "820 × 312 px" },
    { name: "Twitter Header", size: "1500 × 500 px" },
    { name: "Instagram Post", size: "1080 × 1080 px" },
    { name: "LinkedIn Banner", size: "1584 × 396 px" },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-oar-blue-dark py-20">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-4 mb-6">
            <img src={JOURNEE_LOGO} alt="Logo Journée" className="w-24 h-24 rounded-lg shadow-lg" />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-white mb-4">
            Kit Média
          </h1>
          <p className="text-xl text-white/80 mb-2">
            Journée Africaine de la Russophonie
          </p>
          <p className="text-oar-gold text-lg font-bold">
            26 Septembre 2027
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-oar-bg-secondary">
        <div className="max-w-6xl mx-auto px-6">
          {/* Introduction */}
          <div className="bg-white border border-oar-border rounded-lg p-8 mb-12 text-center">
            <Calendar className="w-12 h-12 text-oar-blue mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-oar-text-primary mb-4">
              À propos de la Journée
            </h2>
            <p className="text-oar-text-secondary max-w-2xl mx-auto">
              La Journée Africaine de la Russophonie, célébrée chaque année le 26 septembre, 
              est un événement majeur qui célèbre les liens culturels, linguistiques et économiques 
              entre l'Afrique et le monde russophone. Utilisez ces ressources pour promouvoir l'événement.
            </p>
          </div>

          {/* Logos Section */}
          <div className="mb-12">
            <h2 className="font-heading text-2xl text-oar-text-primary mb-6 flex items-center gap-2">
              <Image className="w-6 h-6 text-oar-blue" />
              Logos officiels
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Logo Journée */}
              <div className="bg-white border border-oar-border rounded-lg p-6">
                <h3 className="font-heading text-lg text-oar-text-primary mb-4">
                  Logo - Journée Africaine de la Russophonie
                </h3>
                <div className="bg-oar-bg-secondary rounded-lg p-8 flex items-center justify-center mb-4">
                  <img src={JOURNEE_LOGO} alt="Logo Journée" className="max-h-48 object-contain" />
                </div>
                <p className="text-oar-text-muted text-sm mb-4">
                  Logo officiel représentant le continent africain avec la date emblématique "26".
                </p>
                <a
                  href={JOURNEE_LOGO}
                  download="logo-journee-russophonie.png"
                  className="flex items-center justify-center gap-2 bg-oar-blue hover:bg-oar-blue-dark text-white rounded-lg px-4 py-2 w-full"
                >
                  <Download className="w-4 h-4" />
                  Télécharger PNG
                </a>
              </div>
              
              {/* Logo OAR */}
              <div className="bg-white border border-oar-border rounded-lg p-6">
                <h3 className="font-heading text-lg text-oar-text-primary mb-4">
                  Logo - Organisation Africaine de la Russophonie
                </h3>
                <div className="bg-oar-bg-secondary rounded-lg p-8 flex items-center justify-center mb-4">
                  <img src={OAR_LOGO} alt="Logo OAR" className="max-h-48 object-contain rounded-lg" />
                </div>
                <p className="text-oar-text-muted text-sm mb-4">
                  Logo officiel de l'OAR, utilisable pour toutes communications officielles.
                </p>
                <a
                  href={OAR_LOGO}
                  download="logo-oar.jpg"
                  className="flex items-center justify-center gap-2 bg-oar-blue hover:bg-oar-blue-dark text-white rounded-lg px-4 py-2 w-full"
                >
                  <Download className="w-4 h-4" />
                  Télécharger JPG
                </a>
              </div>
            </div>
          </div>

          {/* Affiche Officielle */}
          <div className="mb-12">
            <h2 className="font-heading text-2xl text-oar-text-primary mb-6 flex items-center gap-2">
              <FileText className="w-6 h-6 text-oar-orange" />
              Affiche Officielle
            </h2>
            <div className="bg-white border border-oar-border rounded-lg overflow-hidden">
              <div className="grid md:grid-cols-2">
                {/* Poster Preview */}
                <div className="bg-oar-blue-dark p-8 flex items-center justify-center min-h-[500px]">
                  <div className="bg-gradient-to-br from-oar-blue via-oar-blue-dark to-[#001a4d] rounded-lg p-8 text-center max-w-sm shadow-2xl">
                    <img src={JOURNEE_LOGO} alt="Logo" className="w-32 h-32 mx-auto mb-6" />
                    <h3 className="text-white font-heading text-2xl mb-2">JOURNÉE AFRICAINE</h3>
                    <h3 className="text-oar-gold font-heading text-2xl mb-4">DE LA RUSSOPHONIE</h3>
                    <div className="bg-oar-red text-white py-3 px-6 rounded-lg mb-4 inline-block">
                      <p className="text-3xl font-bold">26 SEPTEMBRE</p>
                      <p className="text-lg">2027</p>
                    </div>
                    <p className="text-white/80 text-sm mb-4">
                      Célébrons ensemble les liens entre l'Afrique et le monde russophone
                    </p>
                    <div className="flex items-center justify-center gap-2">
                      <img src={OAR_LOGO} alt="OAR" className="w-8 h-8 rounded" />
                      <span className="text-white text-sm">OAR - Organisation Africaine de la Russophonie</span>
                    </div>
                  </div>
                </div>
                
                {/* Description */}
                <div className="p-8">
                  <h3 className="font-heading text-xl text-oar-text-primary mb-4">
                    Affiche A3 / A4
                  </h3>
                  <p className="text-oar-text-secondary mb-6">
                    Affiche officielle pour la promotion de la Journée Africaine de la Russophonie. 
                    Disponible en format A3 (297 × 420 mm) et A4 (210 × 297 mm).
                  </p>
                  
                  <div className="space-y-4 mb-6">
                    <div className="flex items-center gap-2 text-oar-text-secondary">
                      <span className="w-2 h-2 bg-oar-blue rounded-full"></span>
                      Design moderne et institutionnel
                    </div>
                    <div className="flex items-center gap-2 text-oar-text-secondary">
                      <span className="w-2 h-2 bg-oar-orange rounded-full"></span>
                      Haute résolution (300 DPI)
                    </div>
                    <div className="flex items-center gap-2 text-oar-text-secondary">
                      <span className="w-2 h-2 bg-oar-gold rounded-full"></span>
                      Prêt pour impression professionnelle
                    </div>
                  </div>
                  
                  <div className="bg-oar-orange-soft border border-oar-orange/30 rounded-lg p-4 mb-6">
                    <p className="text-oar-orange text-sm">
                      <strong>Note :</strong> Les fichiers haute résolution peuvent être demandés à 
                      contact@russophonie-afrique.com
                    </p>
                  </div>
                  
                  <button className="flex items-center justify-center gap-2 bg-oar-orange hover:bg-oar-orange-dark text-white rounded-lg px-6 py-3 w-full">
                    <Download className="w-5 h-5" />
                    Demander les fichiers HD
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Charte Graphique */}
          <div className="mb-12">
            <h2 className="font-heading text-2xl text-oar-text-primary mb-6 flex items-center gap-2">
              <Palette className="w-6 h-6 text-oar-green" />
              Charte Graphique
            </h2>
            
            {/* Colors */}
            <div className="bg-white border border-oar-border rounded-lg p-6 mb-6">
              <h3 className="font-heading text-lg text-oar-text-primary mb-4">Palette de couleurs</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {colors.map((color) => (
                  <div key={color.hex} className="text-center">
                    <div 
                      className="w-full h-20 rounded-lg shadow-md mb-2 flex items-end justify-center pb-2"
                      style={{ backgroundColor: color.hex, color: color.textColor }}
                    >
                      <span className="text-xs font-mono">{color.hex}</span>
                    </div>
                    <p className="text-sm text-oar-text-secondary">{color.name}</p>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Typography */}
            <div className="bg-white border border-oar-border rounded-lg p-6 mb-6">
              <h3 className="font-heading text-lg text-oar-text-primary mb-4">Typographie</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="text-oar-text-muted text-sm mb-2">Titres</p>
                  <p className="font-heading text-3xl text-oar-text-primary">Playfair Display</p>
                  <p className="text-oar-text-secondary text-sm mt-2">
                    Police avec empattements pour les titres et en-têtes.
                  </p>
                </div>
                <div>
                  <p className="text-oar-text-muted text-sm mb-2">Corps de texte</p>
                  <p className="text-3xl text-oar-text-primary">Inter</p>
                  <p className="text-oar-text-secondary text-sm mt-2">
                    Police sans empattements pour le corps de texte.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Social Formats */}
            <div className="bg-white border border-oar-border rounded-lg p-6">
              <h3 className="font-heading text-lg text-oar-text-primary mb-4">Formats réseaux sociaux</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {socialBanners.map((banner) => (
                  <div key={banner.name} className="bg-oar-bg-secondary rounded-lg p-4 text-center">
                    <p className="font-medium text-oar-text-primary mb-1">{banner.name}</p>
                    <p className="text-sm text-oar-text-muted">{banner.size}</p>
                  </div>
                ))}
              </div>
              <p className="text-oar-text-muted text-sm mt-4 text-center">
                Demandez les bannières personnalisées à contact@russophonie-afrique.com
              </p>
            </div>
          </div>

          {/* Usage Guidelines */}
          <div className="bg-white border border-oar-border rounded-lg p-8">
            <h2 className="font-heading text-2xl text-oar-text-primary mb-6">
              Directives d'utilisation
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-heading text-lg text-oar-green mb-3">✓ À faire</h3>
                <ul className="space-y-2 text-oar-text-secondary">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-oar-green rounded-full mt-2"></span>
                    Utiliser les logos sur un fond uni contrastant
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-oar-green rounded-full mt-2"></span>
                    Maintenir les proportions originales des logos
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-oar-green rounded-full mt-2"></span>
                    Respecter l'espace minimum autour des logos
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-oar-green rounded-full mt-2"></span>
                    Mentionner l'OAR dans les communications officielles
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-heading text-lg text-oar-red mb-3">✗ À éviter</h3>
                <ul className="space-y-2 text-oar-text-secondary">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-oar-red rounded-full mt-2"></span>
                    Déformer ou étirer les logos
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-oar-red rounded-full mt-2"></span>
                    Modifier les couleurs officielles
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-oar-red rounded-full mt-2"></span>
                    Ajouter des effets (ombres, reflets) non autorisés
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-oar-red rounded-full mt-2"></span>
                    Utiliser les logos sur des fonds complexes
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="mt-12 text-center">
            <p className="text-oar-text-muted mb-4">
              Pour toute demande spécifique ou fichiers haute résolution :
            </p>
            <a 
              href="mailto:contact@russophonie-afrique.com"
              className="inline-flex items-center gap-2 text-oar-blue hover:text-oar-blue-dark font-medium"
            >
              <ExternalLink className="w-4 h-4" />
              contact@russophonie-afrique.com
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default MediaKitPage;
